import type RewardsSpendingRulesApiService from "@apps/rewards/api_services/RewardsSpendingRulesApiService/RewardsSpendingRulesApiService";
import type RewardsCustomerRewardsApiService from "@apps/rewards/modules/CustomerRewards/api_service/RewardsCustomerRewardsApiService";
import type RewardsProgramAccountApiService from "@apps/rewards/modules/ProgramAccount/api_service/RewardsProgramAccountApiService";
import type RewardsVipTiersApiService from "@apps/rewards/modules/VipTiers/api_service/RewardsVipTiersApiService";
import type RewardsModalManager from "@apps/rewards/utils/RewardsModalManager/RewardsModalManager";
import { subscribeToSdkMethod } from "@modules/sdk/helpers";
import { lastValueFrom } from "rxjs";

import { REWARDS_SDK_METHODS } from "../constants";
import type GetCustomerActiveRewardsSdkMethodPayload from "../methods/GetCustomerActiveRewardsSdkMethodPayload";
import type GetRewardsProgramAccountSdkMethodPayload from "../methods/GetRewardsProgramAccountSdkMethodPayload";
import type GetRewardsSpendingRulesSdkMethodPayload from "../methods/GetRewardsSpendingRulesSdkMethodPayload";
import type GetRewardsVipTiersSdkMethodPayload from "../methods/GetRewardsVipTiersSdkMethodPayload";
import type OpenRewardsModalSdkMethodPayload from "../methods/OpenRewardsModalSdkMethodPayload";
import type RedeemRewardSdkMethodPayload from "../methods/RedeemRewardSdkMethodPayload";

interface RewardsServices {
    rewardsProgramAccountApiService: RewardsProgramAccountApiService;
    rewardsSpendingRulesApiService: RewardsSpendingRulesApiService;
    rewardsCustomerRewardsApiService: RewardsCustomerRewardsApiService;
    rewardsVipTiersApiService: RewardsVipTiersApiService;
    rewardsModalManager: RewardsModalManager;
}

export const subscribeToRewardsSdkMethods = ({
    rewardsProgramAccountApiService,
    rewardsSpendingRulesApiService,
    rewardsCustomerRewardsApiService,
    rewardsVipTiersApiService,
    rewardsModalManager,
}: RewardsServices): void => {
    subscribeToSdkMethod<GetRewardsProgramAccountSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_PROGRAM_ACCOUNT,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                rewardsProgramAccountApiService.getRewardsProgramAccount({
                    locale,
                })
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetRewardsSpendingRulesSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_SPENDING_RULES,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                rewardsSpendingRulesApiService.getSpendingRules({ locale })
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<RedeemRewardSdkMethodPayload>(
        REWARDS_SDK_METHODS.REDEEM_REWARD,
        ({ params, onSuccess, onError }) => {
            lastValueFrom(rewardsSpendingRulesApiService.redeem(params))
                .then(() => onSuccess())
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetCustomerActiveRewardsSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_CUSTOMER_ACTIVE_REWARDS,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(
                rewardsCustomerRewardsApiService.getCustomerActiveRewards({
                    locale,
                })
            )
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<GetRewardsVipTiersSdkMethodPayload>(
        REWARDS_SDK_METHODS.GET_REWARDS_VIP_TIERS,
        ({ locale, onSuccess, onError }) => {
            lastValueFrom(rewardsVipTiersApiService.getTiers({ locale }))
                .then((result) => onSuccess(result))
                .catch((error: Error) => onError(error));
        }
    );

    subscribeToSdkMethod<OpenRewardsModalSdkMethodPayload>(
        REWARDS_SDK_METHODS.OPEN_REWARDS_MODAL,
        ({ modalName, params, onSuccess, onError }) => {
            rewardsModalManager
                .openRewardsModal(modalName, params)
                .then(() => onSuccess(params))
                .catch((error: Error) => onError(error));
        }
    );
};
